import React from "react";
import ReactGA from "react-ga";
import {
  FaWhatsappSquare,
  FaTwitterSquare,
  FaInstagramSquare,
  FaFacebookSquare,
} from "react-icons/fa";

import SiteNavbar from "@/components/SiteNavbar";
import Section from "@/components/Section";
import AboutItemComponent from "@/components/AboutItemComponent";
import ServiceItemComponent from "@/components/ServiceItemComponent";
import SectionHeaderComponent from "@/components/SectionHeaderComponent";
import ValueItemComponent from "@/components/ValueItemComponent";
import PortfolioItemComponent from "@/components/PortfolioItemComponent";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { link, text } from "@/constants/tailwind-theme";

import aboutus from "@/assets/images/aboutus.jpg";
import banner from "@/assets/images/banner.jpg";
import desktopicon from "@/assets/icons/iconmonstr-smartphone-13-240.png";
import shopicon from "@/assets/icons/shop.png";
import websiteicon from "@/assets/icons/iconmonstr-computer-2-240.png";
import aiicon from "@/assets/icons/icons8-ai-96.png";
import ghanakids from "@/assets/images/ghanakids.jpg";
import contactus from "@/assets/images/contactus.png";
import africa from "@/assets/icons/africa.png";
import efficiency from "@/assets/icons/efficiency.png";
import goaloriented from "@/assets/icons/goaloriented.png";
import timelydelivery from "@/assets/icons/timelydelivery.png";
import portfolioimage from "@/assets/images/IMG_2901.jpg";
import portfolioimage2 from "@/assets/images/portfolio2.jpeg";
import portfolioimage4 from "@/assets/images/IMG_2902.jpg";
import portfolioimage5 from "@/assets/images/portfolio4.jpeg";
import portfolioSamselsage from "@/assets/images/samselsage.png";
import portfolioStockupai from "@/assets/images/stockupai.jpg";

const navLinks = [
  { href: "#home", label: "Home" },
  { href: "#about", label: "About Us" },
  { href: "#services", label: "Our Services" },
  { href: "#values", label: "Why Choose Us" },
  { href: "#work", label: "Our Work" },
  { href: "#contact", label: "Contact Us" },
];

const WORK_FILTERS = [
  { key: "ALL", label: "ALL" },
  { key: "INHOUSE", label: "IN-HOUSE" },
  { key: "CLIENT", label: "CLIENTS" },
];

const HomePage = () => {
  const [selectedWork, setSelectedWork] = React.useState("ALL");

  return (
    <div className="flex flex-col">
      <SiteNavbar />

      {/* HERO */}
      <Section
        id="home"
        backgroundImage={banner}
        overlay
        className="flex min-h-[420px] flex-col justify-center md:min-h-[500px]"
      >
        <div className="mx-auto grid max-w-7xl gap-y-6 px-6 py-16 md:w-8/12 md:px-10 md:py-20">
          <h1 className={cn(text.heading1, "text-white")}>
            Welcome to HEYPHORD I.T SOLUTIONS
          </h1>
          <p className={cn(text.heading2, "text-white/95")}>
            Our mission is to apply technology to solve real-world problems for
            individuals, communities, and organizations.
          </p>
          <p className={cn(text.heading2, "text-white/95")}>
            Let us help you resolve your organization's software-related issues
            while saving you cost.
          </p>
          <Button asChild variant="secondary" size="lg" className="mt-2 w-fit">
            <a href="#contact">Contact Us</a>
          </Button>
        </div>
      </Section>

      {/* ABOUT */}
      <Section id="about" className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-16 md:grid-cols-2 md:px-10 md:py-20">
          <AboutItemComponent
            title="ABOUT US"
            description="HEYPHORD I.T SOLUTIONS is a registered Ghanaian business that deals in the sales, lease, and creation of software applications for private and commercial use."
          />
          <AboutItemComponent
            title="MISSION"
            description="At HEYPHORD I.T SOLUTIONS, our mission is to apply technology to solve real-world problems for individuals, communities, and organizations."
          />
          <AboutItemComponent
            title="VISION"
            description="Our vision is to improve day-to-day life and make a meaningful impact on individuals, communities, and organizations through our technology solutions."
          />
          <div className="overflow-hidden rounded-lg md:col-start-2 md:row-start-1 md:row-span-3">
            <img
              src={aboutus}
              className="h-full w-full object-cover"
              alt="Heyphord team at work"
            />
          </div>
        </div>
      </Section>

      {/* SERVICES */}
      <Section id="services" className="bg-muted">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-20">
          <div className="mb-10">
            <SectionHeaderComponent
              title="OUR SERVICES"
              description="Wondering how we can be of help to you? Here are some of the services we provide to make life easier"
            />
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <ServiceItemComponent
              icon={aiicon}
              title="AI SERVICES"
              description="We design and integrate AI solutions that automate work, surface insights, and add intelligent features to your products."
            />
            <ServiceItemComponent
              type="secondary"
              icon={websiteicon}
              title="WEBSITE DEVELOPMENT"
              description="Enjoy a smooth and seamless experience with our responsive website solutions."
            />
            <ServiceItemComponent
              icon={shopicon}
              title="SALE AND LEASE OF COMMERCIAL SOFTWARES"
              description="Customized software products developed for your convenience."
            />
            <ServiceItemComponent
              type="secondary"
              icon={desktopicon}
              title="MOBILE APP DEVELOPMENT"
              description="We develop mobile and desktop applications, compatible with Android And iOS."
            />
          </div>
        </div>
      </Section>

      {/* VALUES */}
      <Section
        id="values"
        backgroundImage={ghanakids}
        overlay
        className="bg-fixed"
      >
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-20">
          <div className="mb-10">
            <SectionHeaderComponent
              type="secondary"
              title="WHY CHOOSE US"
              description="Our values set us apart from the competition. See why we should be your first choice."
            />
          </div>
          <div className="grid gap-10 md:grid-cols-2">
            <ValueItemComponent
              icon={goaloriented}
              title="GOAL ORIENTED"
              description="We take satisfaction in providing solutions that best suit our clients' needs. Hence, we prioritize understanding the problem on the ground and developing a goal and an actionable strategy to solve that problem."
            />
            <ValueItemComponent
              icon={desktopicon}
              title="CONFIDENTIALITY"
              description="We understand that client information is of utmost confidentiality. Unless it is necessary, we do not share our client's personal information or anything regarding their projects with a third party. In situations where we have to, we seek the consent of our clients."
            />
            <ValueItemComponent
              icon={timelydelivery}
              title="TIMELY DELIVERY"
              description="Trust us to deliver on time. Our team works assiduously to deliver your software solutions without any delay."
            />
            <ValueItemComponent
              icon={efficiency}
              title="EFFICIENCY"
              description="Our solutions are designed to deliver the best results at affordable rates. We optimize the available resources to achieve excellent results."
            />
            <ValueItemComponent
              icon={africa}
              title="PROUDLY AFRICAN"
              description="We want to use our products to change the narrative for Africa and the world at large. We want to make quality and affordable products that can compete in the international market."
            />
          </div>
        </div>
      </Section>

      {/* PORTFOLIO */}
      <Section id="work">
        <div className="mx-auto max-w-7xl space-y-10 px-6 py-16 md:px-10 md:py-20">
          <SectionHeaderComponent
            title="OUR WORK"
            description="Have a look at some of our in-house projects, as well as some projects our clients have allowed us to showcase. Click on the images to see more details."
          />

          <div className="flex flex-wrap gap-3">
            {WORK_FILTERS.map(({ key, label }) => (
              <Button
                key={key}
                variant={selectedWork === key ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedWork(key)}
              >
                {label}
              </Button>
            ))}
          </div>

          <div className="flex flex-row flex-wrap gap-6">
            <PortfolioItemComponent
              selectedTag={selectedWork}
              tags={["ALL", "INHOUSE"]}
              image={portfolioStockupai}
              title="STOCKUPAI POS"
              url="https://stockupai.heyphord.com/"
            />
            <PortfolioItemComponent
              selectedTag={selectedWork}
              tags={["ALL", "CLIENT"]}
              image={portfolioSamselsage}
              title="SAMSEL SAGE"
              url="https://samselsage.com/"
            />
            <PortfolioItemComponent
              selectedTag={selectedWork}
              tags={["ALL", "INHOUSE"]}
              image={portfolioimage}
              title="VOTE BY HEYPHORD"
              url="https://vote.heyphord.com"
            />
            <PortfolioItemComponent
              selectedTag={selectedWork}
              tags={["ALL", "CLIENT"]}
              image={portfolioimage2}
              title="DIVCON LIMITED"
              url="https://divconlimited.com"
            />
            <PortfolioItemComponent
              selectedTag={selectedWork}
              tags={["ALL", "CLIENT"]}
              image={portfolioimage4}
              title="JANIEHAYS LIMITED"
              url="https://janiehays.com"
            />
            <PortfolioItemComponent
              selectedTag={selectedWork}
              tags={["ALL", "CLIENT"]}
              image={portfolioimage5}
              title="THEMP FOUNDATION"
              url="https://thempproject.org"
            />
          </div>
        </div>
      </Section>

      {/* CONTACT */}
      <Section
        id="contact"
        backgroundImage={contactus}
        overlay
        className="bg-fixed"
      >
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-20">
          <div className="mb-10">
            <SectionHeaderComponent
              type="secondary"
              title="CONTACT US"
              description="Do you have any inquiries? Go ahead and talk to us. Don't forget to follow our social media handles for more updates."
            />
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <Card className="border-white/20 bg-white/10 text-white backdrop-blur-sm">
              <CardHeader>
                <CardTitle className={cn(text.heading2, "text-white")}>
                  HEYPHORD I.T SOLUTIONS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className={text.heading3 + " text-white"}>Madina New Road</p>
                <p className={text.heading3 + " text-white"}>Accra, Ghana</p>
                <a
                  href="tel:+233530466842"
                  className={cn(link.heading3, "block text-white hover:text-white/80")}
                >
                  +233530466842
                </a>
                <a
                  href="mailto:contact@heyphord.com"
                  className={cn(link.heading3, "block text-white hover:text-white/80")}
                >
                  contact@heyphord.com
                </a>
              </CardContent>
            </Card>

            <Card className="border-white/20 bg-white/10 text-white backdrop-blur-sm">
              <CardHeader>
                <CardTitle className={cn(text.heading2, "text-white")}>
                  Social Media
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  <a
                    onClick={() => {
                      ReactGA.event({ category: "CONTACT_US", action: "WHATSAPP" });
                    }}
                    href="https://wa.me/233530466842"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-opacity hover:opacity-75"
                    aria-label="WhatsApp"
                  >
                    <FaWhatsappSquare color="white" size={40} />
                  </a>
                  <a
                    onClick={() => {
                      ReactGA.event({ category: "CONTACT_US", action: "INSTAGRAM" });
                    }}
                    href="https://www.instagram.com/heyphord_solns/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-opacity hover:opacity-75"
                    aria-label="Instagram"
                  >
                    <FaInstagramSquare color="white" size={40} />
                  </a>
                  <a
                    onClick={() => {
                      ReactGA.event({ category: "CONTACT_US", action: "TWITTER" });
                    }}
                    href="https://twitter.com/heyphord_solns"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-opacity hover:opacity-75"
                    aria-label="Twitter"
                  >
                    <FaTwitterSquare color="white" size={40} />
                  </a>
                  <a
                    onClick={() => {
                      ReactGA.event({ category: "CONTACT_US", action: "FACEBOOK" });
                    }}
                    href="https://www.facebook.com/people/Heyphord-IT-Solutions/100086574910603/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-opacity hover:opacity-75"
                    aria-label="Facebook"
                  >
                    <FaFacebookSquare color="white" size={40} />
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/20 bg-white/10 text-white backdrop-blur-sm">
              <CardHeader>
                <CardTitle className={cn(text.heading2, "text-white")}>
                  Links
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {navLinks.map(({ href, label }) => (
                  <a
                    key={href}
                    href={href}
                    className={cn(link.heading3, "block text-white hover:text-white/80")}
                  >
                    &#x2022; {label.toUpperCase()}
                  </a>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="bg-primary-500 px-6 py-6 text-center">
        <p className="text-xs text-white/90">
          Designed &amp; Developed By Heyphord I.T Solutions
        </p>
      </footer>
    </div>
  );
};

export default HomePage;
